// API utility for fetching Weather Factor data

import { useCallback } from 'react';

const API_BASE_URL = 'https://xo2a24ey5h.execute-api.eu-west-1.amazonaws.com';

// TypeScript interfaces
export interface WaypointData {
  lat: number;
  lon: number;
}

export interface MonthlySpeedloss {
  jan: number;
  feb: number;
  mar: number;
  apr: number;
  may: number;
  jun: number;
  jul: number;
  aug: number;
  sep: number;
  oct: number;
  nov: number;
  dec: number;
}

export interface WeatherFactorLeg {
  route: string;
  condition: string;
  nm: number;
  speedloss: MonthlySpeedloss;
  activeMonths: string[];
}

export interface AverageWeatherFactor {
  type: string;
  route: string;
  wf: number;
  nm: number;
  imos_nm: number;
  voycalc_nm: number;
}

export interface WeatherFactorResponse {
  wp: WaypointData[] | WaypointData[][];
  wf: WeatherFactorLeg[];
  avgwf: AverageWeatherFactor[];
}

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  message?: string;
}

export const useWeatherFactorApi = () => {
  const fetchWeatherFactor = useCallback(
    async (id: string, src?: string | null): Promise<ApiResult<WeatherFactorResponse>> => {
      const params = new URLSearchParams({ type: 'wf', id });
      if (src) params.set('src', src);
      const url = `${API_BASE_URL}/api?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (!response.ok) {
          return {
            ok: false,
            status: response.status,
            message: data.error || 'Failed to fetch weather factor data',
          };
        }

        return {
          ok: true,
          status: response.status,
          data,
        };
      } catch (error) {
        return {
          ok: false,
          status: 0,
          message: error instanceof Error ? error.message : 'Network error',
        };
      }
    },
    []
  );

  return { fetchWeatherFactor };
};

// Month keys for iteration
export const MONTH_KEYS: (keyof MonthlySpeedloss)[] = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
];

// Month display names
export const MONTH_NAMES: Record<keyof MonthlySpeedloss, string> = {
  jan: 'Jan',
  feb: 'Feb',
  mar: 'Mar',
  apr: 'Apr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  aug: 'Aug',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dec: 'Dec',
};
